<template>
  <aside :class="$attrs.class" aria-label="Sidebar">
    <div class="bg-bgr rounded-md will-change-contents">
      <RedesignFilterFieldset
        v-if="selected.length"
        :label="$t(`${trPrefixFacets}selected`)"
      >
        <RedesignFilterSelected v-model="model" />
      </RedesignFilterFieldset>

      <RedesignFilterFieldset
        v-if="filters.freeweekCategory.enabled"
        :label="$t(`${trPrefixFacets}freeweekCategory`)"
        class="flex flex-col"
        data-scroll-anchor
      >
        <RedesignMultiSelectInput
          v-model="filters.freeweekCategory.value"
          name="freeweekCategory"
          :options="filters.freeweekCategory.options"
          :loading="filters.freeweekCategory.loading"
          :loading-items="3"
        >
          <template #item="{ option }">
            {{ getSearchTranslation('freeweekCategory.', option.value) }}
          </template>
        </RedesignMultiSelectInput>
      </RedesignFilterFieldset>

      <RedesignFilterFieldset
        v-if="filters.bedrooms.enabled || filters.bathrooms.enabled"
        :label="$t(`${trPrefixFacets}rooms`)"
        data-scroll-anchor
      >
        <div class="flex gap-y-4 gap-x-8 flex-wrap">
          <RedesignStepperInput
            v-if="filters.bedrooms.enabled"
            v-model="filters.bedrooms.value"
            name="bedrooms"
            :options="filters.bedrooms.options"
            :loading="filters.bedrooms.loading"
          >
            {{ $t(`${trPrefixFacets}bedrooms`) }}
          </RedesignStepperInput>

          <RedesignStepperInput
            v-if="filters.bathrooms.enabled"
            v-model="filters.bathrooms.value"
            name="bathrooms"
            :options="filters.bathrooms.options"
            :loading="filters.bathrooms.loading"
          >
            {{ $t(`${trPrefixFacets}bathrooms`) }}
          </RedesignStepperInput>
        </div>
      </RedesignFilterFieldset>

      <RedesignFilterFieldset
        v-if="filters.priceRange.enabled"
        :label="$t(`${trPrefixFacets}priceRange`)"
        data-scroll-anchor
      >
        <RedesignWebccPriceRangePicker
          v-model="filters.priceRange.value"
          :currency
          name="price"
          :options="filters.priceRange.options"
          :loading="filters.priceRange.loading"
        />
      </RedesignFilterFieldset>

      <RedesignFilterFieldset
        v-if="filters.accommodationType.enabled"
        :label="$t(`${trPrefixFacets}accommodationType`)"
        data-scroll-anchor
      >
        <RedesignSingleSelectInput
          v-model="filters.accommodationType.value"
          name="accommodationType"
          :options="filters.accommodationType.options"
          :loading="filters.accommodationType.loading"
          :loading-items="3"
        >
          <template #item="{ option }">
            {{
              getSearchTranslation('accommodationType.', option.value as string)
            }}
            ({{ option.count }})
          </template>
        </RedesignSingleSelectInput>
      </RedesignFilterFieldset>

      <RedesignFilterFieldset
        v-if="filters.propertyType.enabled"
        :label="$t(`${trPrefixFacets}propertyType`)"
        class="flex flex-col"
        data-scroll-anchor
      >
        <RedesignMultiSelectInput
          v-model="filters.propertyType.value"
          name="propertyType"
          :options="filters.propertyType.options"
          :loading="filters.propertyType.loading"
          :loading-items="4"
        >
          <template #item="{ option }">
            {{ getSearchTranslation('propertyType.', option.value) }}
            ({{ option.count }})
          </template>
        </RedesignMultiSelectInput>
      </RedesignFilterFieldset>

      <RedesignFilterFieldset
        v-if="filters.features.enabled"
        :label="$t(`${trPrefixFacets}features`)"
        class="flex flex-col"
        data-scroll-anchor
      >
        <RedesignMultiSelectInput
          v-model="filters.features.value"
          name="features"
          :options="filters.features.options"
          :loading="filters.features.loading"
          :loading-items="5"
          :default-visible-count="5"
        >
          <template #item="{ option }">
            {{ getSearchTranslation('features.', option.value) }}
            ({{ option.count }})
          </template>
        </RedesignMultiSelectInput>
      </RedesignFilterFieldset>

      <RedesignFilterFieldset
        v-if="filters.pool.enabled"
        :label="$t(`${trPrefixFacets}pool`)"
        data-scroll-anchor
      >
        <RedesignSingleSelectInput
          v-model="filters.pool.value"
          name="pool"
          :options="filters.pool.options"
          :loading="filters.pool.loading"
          :loading-items="4"
        >
          <template #item="{ option }">
            {{ getSearchTranslation('pool.', option.value as string) }}
            ({{ option.count }})
          </template>
        </RedesignSingleSelectInput>
      </RedesignFilterFieldset>

      <RedesignFilterFieldset
        v-if="filters.starRating.enabled"
        :label="$t(`${trPrefixFacets}starRating`)"
        class="flex flex-col"
        data-scroll-anchor
      >
        <RedesignSingleSelectInput
          v-model="filters.starRating.value"
          name="starRating"
          :options="filters.starRating.options"
          :loading="filters.starRating.loading"
          :loading-items="4"
        >
          <template #item="{ option }">
            <RedesignWebccStars :stars="option.value" full>
              {{ option.value + (option.value < 5 ? '+' : '') }}
              ({{ option.count }})
            </RedesignWebccStars>
          </template>
        </RedesignSingleSelectInput>
      </RedesignFilterFieldset>

      <RedesignFilterFieldset
        v-if="isAnyDistanceFilterEnabled"
        :label="$t(`${trPrefixFacets}distances`)"
        data-scroll-anchor
      >
        <template v-if="firstActiveDistanceFilter">
          <h5 class="mb-4 text-base font-medium">
            {{
              $t(
                `${trPrefixFacets}${firstActiveDistanceFilter}` as TranslationKey,
              )
            }}
          </h5>
          <RedesignSingleSelectInput
            v-model="filters[firstActiveDistanceFilter].value"
            :name="`distance-${firstActiveDistanceFilter}`"
            :options="filters[firstActiveDistanceFilter].options"
            :loading="filters[firstActiveDistanceFilter].loading"
            :loading-items="5"
          >
            <template #item="{ option }">
              {{ getSearchTranslation('distances.', option.value as number) }}
              ({{ option.count }})
            </template>
          </RedesignSingleSelectInput>
        </template>

        <RedesignWebccDisclosure
          v-if="disclosureDistanceFilters.length"
          class="mt-4 grid gap-4"
          name="distances"
        >
          <template
            v-for="filterKey in disclosureDistanceFilters"
            :key="filterKey"
          >
            <h5 class="text-base font-medium">
              {{ $t(`${trPrefixFacets}${filterKey}` as TranslationKey) }}
            </h5>
            <RedesignSingleSelectInput
              v-model="filters[filterKey].value"
              :name="`distance-${filterKey}`"
              :options="filters[filterKey].options"
              :loading="filters[filterKey].loading"
              :loading-items="5"
            >
              <template #item="{ option }">
                {{ getSearchTranslation('distances.', option.value as number) }}
                ({{ option.count }})
              </template>
            </RedesignSingleSelectInput>
          </template>
        </RedesignWebccDisclosure>
      </RedesignFilterFieldset>

      <RedesignFilterFieldset
        v-if="filters.offers.enabled"
        :label="$t(`${trPrefixFacets}offers`)"
        data-scroll-anchor
      >
        <RedesignSingleSelectInput
          v-model="filters.offers.value"
          name="offers"
          :options="filters.offers.options"
          :loading="filters.offers.loading"
          :loading-items="5"
        >
          <template #item="{ option }">
            {{ getSearchTranslation('offers.', option.value as string) }}
            ({{ option.count }})
          </template>
        </RedesignSingleSelectInput>
      </RedesignFilterFieldset>

      <RedesignFilterFieldset
        v-if="filters.holidayType.enabled"
        :label="$t(`${trPrefixFacets}holidayType`)"
        data-scroll-anchor
      >
        <RedesignSingleSelectInput
          v-model="filters.holidayType.value"
          name="holidayType"
          :options="filters.holidayType.options"
          :loading="filters.holidayType.loading"
          :loading-items="5"
        >
          <template #item="{ option }">
            {{ getSearchTranslation('holidayType.', option.value as string) }}
            ({{ option.count }})
          </template>
        </RedesignSingleSelectInput>
      </RedesignFilterFieldset>

      <RedesignFilterFieldset
        v-if="filters.special.enabled"
        :label="$t(`${trPrefixFacets}special`)"
        class="flex flex-col"
        data-scroll-anchor
      >
        <RedesignMultiSelectInput
          v-model="filters.special.value"
          name="special"
          :options="filters.special.options"
          :loading="filters.special.loading"
          :loading-items="5"
          :default-visible-count="5"
        >
          <template #item="{ option }">
            {{ getSearchTranslation('special.', option.value) }}
            ({{ option.count }})
          </template>
        </RedesignMultiSelectInput>
      </RedesignFilterFieldset>

      <RedesignFilterFieldset
        v-if="filters.fishing.enabled"
        :label="$t(`${trPrefixFacets}fishing`)"
        class="flex flex-col"
        data-scroll-anchor
      >
        <RedesignMultiSelectInput
          v-model="filters.fishing.value"
          name="fishing"
          :options="filters.fishing.options"
          :loading="filters.fishing.loading"
          :loading-items="3"
        >
          <template #item="{ option }">
            {{ getSearchTranslation('fishing.', option.value) }}
            ({{ option.count }})
          </template>
        </RedesignMultiSelectInput>
      </RedesignFilterFieldset>

      <RedesignFilterFieldset
        v-if="filters.activities.enabled"
        :label="$t(`${trPrefixFacets}activities`)"
        class="flex flex-col"
        data-scroll-anchor
      >
        <RedesignMultiSelectInput
          v-model="filters.activities.value"
          name="activities"
          :options="filters.activities.options"
          :loading="filters.activities.loading"
          :loading-items="5"
          :default-visible-count="6"
          class="grid gap-4"
        >
          <template #item="{ option }">
            {{ getSearchTranslation('activities.', option.value) }}
            ({{ option.count }})
          </template>
        </RedesignMultiSelectInput>
      </RedesignFilterFieldset>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { camelCase, find } from 'lodash-es'

const trPrefixFacets = 'www.facets.'
const trPrefixSearch = 'www.search.'

const model = defineModel<SearchState>({ required: true })

const { all: filters, selected, removeBatch } = useFilters(model)
const { currency } = storeToRefs(useConfdata())
const { t } = useI18n()

function getSearchTranslation(parentKey: string, key: string | number) {
  return t(
    `${trPrefixSearch}${parentKey}${camelCase(key.toString())}` as TranslationKey,
  )
}

/**
 * IHGWEBCC-1789 Hide distance filter if it doesn't exist for a destination
 */
const firstActiveDistanceFilter = computed(
  () =>
    find(
      DISTANCE_FILTERS,
      (filterName) => filters[filterName as keyof typeof filters].enabled,
    ) || null,
)

const isAnyDistanceFilterEnabled = computed(() =>
  DISTANCE_FILTERS.some((key) => filters[key].enabled),
)

const disclosureDistanceFilters = computed(() =>
  DISTANCE_FILTERS.filter(
    (key) => filters[key].enabled && key !== firstActiveDistanceFilter.value,
  ),
)

const unavailableDistanceFilters = computed(() =>
  DISTANCE_FILTERS.filter(
    (filterKey) =>
      filters[filterKey] &&
      !filters[filterKey].enabled &&
      model.value[filterKey],
  ),
)

watch(
  unavailableDistanceFilters,
  (unavailable) => {
    if (unavailable.length > 0) {
      removeBatch(unavailable)
    }
  },
  { immediate: true },
)
</script>
